import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Pencil, Trash2, MoreHorizontal, Mail, UserX, Link2, Clock, Upload, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useSortableTable } from '@/components/shared/SortableTable';
import { formatRelative } from '@/lib/dateUtils';
import { ResponsiveTable, TABLE_COL, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { ListTablePanel } from '@/components/shared/ListTablePanel';
import { AddressAutocomplete } from '@/components/shared/AddressAutocomplete';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { CompanyPlanTab } from '@/components/settings/CompanyPlanTab';
import type { TeamMember, Permission, CompanyTeam } from '@/types';

type MemberType = 'internal' | 'external';

const permissionBadge: Record<Permission, string> = {
  admin: 'bg-purple-500/10 text-purple-500',
  write: 'bg-success/10 text-success',
  read:  'bg-muted text-muted-foreground',
};
const permissionLabel: Record<Permission, string> = {
  admin: 'Admin',
  write: 'Write',
  read:  'Read',
};

function CompanyHeader({
  name,
  publicName,
  logoUrl,
}: {
  name: string;
  publicName: string;
  logoUrl?: string;
}) {
  const displayName = name.trim() || '—';
  const subtitle = publicName.trim() && publicName.trim() !== displayName ? publicName.trim() : null;

  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-gradient-to-br from-brand-primary/10 via-brand-accent/5 to-transparent">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-brand-accent/20 via-transparent to-transparent pointer-events-none" />
      <div className="relative px-6 py-6 sm:px-8 sm:py-8">
        <div className="flex items-start gap-4 sm:gap-6">
          <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-lg bg-background border-4 border-background shadow-md overflow-hidden flex items-center justify-center text-2xl sm:text-3xl font-semibold text-brand-primary shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${displayName} logo`}
                className="h-full w-full object-contain p-1"
              />
            ) : (
              <span>{displayName[0]?.toUpperCase() || '?'}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-display font-semibold truncate">{displayName}</h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground truncate mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const SettingsPage = () => {
  const { selectedCompany } = useAuth();
  const { t } = useLanguage();
  const hook = useCompanySettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // v3.2 (21/05/26): "subscription" tab moved to /profile?tab=billing.
  // Drop it from the valid list but keep the legacy redirect for any
  // bookmark / email link that still points here.
  //
  // v3.2 polish (22/05/26): added "plan" tab — read-only view of the
  // company's current plan + per-company caps + inherited features.
  // Non-owners can see WHO controls the subscription without being
  // able to change it from here (subscription mgmt stays on profile).
  const VALID_TABS = ['general', 'teams', 'members', 'plan'];

  // Legacy redirect: /settings?tab=subscription → /profile?tab=billing
  // v3.5.2 polish (26/05/26): preserva i query param (checkout=success&plan=...)
  // così il SubscriptionTab.useEffect post-checkout riesce a parsarli.
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab === 'subscription') {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'billing');
      navigate(`/profile?${next.toString()}`, { replace: true });
      return;
    }
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== hook.activeTab) {
      hook.setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const resolveMemberTeamLabel = useCallback(
    (m: TeamMember) =>
      m.teamIds.length > 0
        ? m.teamIds.map(tid => hook.teams.find(t => t.id === tid)?.name || tid).join(', ')
        : '—',
    [hook.teams],
  );

  const { sorted, sort, toggleSort } = useSortableTable(
    hook.filteredMembers,
    { column: 'joinedAt', direction: 'desc' },
    {
      getSortValue: (item, column) => {
        if (column === 'team') return resolveMemberTeamLabel(item);
        return (item as Record<string, unknown>)[column];
      },
    },
  );

  type MemberRow = (typeof hook.filteredMembers)[number];

  const membersColumns: ResponsiveTableColumn<MemberRow>[] = [
    {
      id: 'displayName',
      label: t('settings.name'),
      priority: 'primary',
      alwaysVisible: true,
      sortable: true,
      ...TABLE_COL.title,
      truncate: true,
      render: (m) => {
        const isPending = m.status === 'pending';
        const isDeclined = m.status === 'declined';
        const isInviteRow = isPending || isDeclined;
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium overflow-hidden shrink-0 ${isInviteRow ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'}`}>
              {!isInviteRow && m.avatarUrl ? (
                <img src={m.avatarUrl} alt={`${m.displayName || 'Team member'} avatar`} className="h-full w-full object-cover" />
              ) : (
                isInviteRow ? '?' : (m.displayName?.[0] || '?')
              )}
            </div>
            <span className={`font-medium truncate ${isInviteRow ? 'text-muted-foreground italic' : ''}`}>
              {isPending ? t('settings.pendingInvite') : isDeclined ? t('settings.declinedInvite') : m.displayName}
            </span>
            {m.isOwner && (
              <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary shrink-0">
                <Crown className="h-3 w-3" />
                Owner
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'email',
      label: t('settings.email'),
      priority: 'secondary',
      sortable: true,
      truncate: true,
      ...TABLE_COL.text,
      render: (m) => (
        <span className="text-muted-foreground truncate block" title={m.email}>{m.email}</span>
      ),
    },
    {
      id: 'permission',
      label: t('settings.role'),
      priority: 'tertiary',
      sortable: true,
      ...TABLE_COL.compact,
      render: (m) => (
        <span className={`text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${permissionBadge[m.permission] ?? permissionBadge.read}`}>
          {permissionLabel[m.permission] ?? m.permission}
        </span>
      ),
    },
    {
      id: 'team',
      label: t('settings.team'),
      priority: 'tertiary',
      sortable: true,
      hideOn: 'mobile',
      ...TABLE_COL.text,
      sortAccessor: (m) => resolveMemberTeamLabel(m),
      render: (m) => (
        <span className="text-xs text-muted-foreground truncate block">{resolveMemberTeamLabel(m)}</span>
      ),
    },
    {
      id: 'type',
      label: t('settings.type'),
      priority: 'tertiary',
      sortable: true,
      hideOn: 'mobile',
      ...TABLE_COL.compact,
      defaultVisible: false,
      render: (m) => (
        <span className="text-xs capitalize text-muted-foreground whitespace-nowrap">
          {m.type === 'external' ? t('settings.external') : t('settings.internal')}
        </span>
      ),
    },
    {
      id: 'status',
      label: t('settings.status'),
      priority: 'tertiary',
      sortable: true,
      hideOn: 'mobile',
      ...TABLE_COL.compact,
      render: (m) => {
        const isPending = m.status === 'pending';
        const isDeclined = m.status === 'declined';
        if (isPending) {
          return (
            <span className="inline-flex items-center gap-1.5 text-xs text-warning whitespace-nowrap">
              <Clock className="h-3 w-3 shrink-0" />
              {t('settings.pending')}
            </span>
          );
        }
        if (isDeclined) {
          return (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
              <UserX className="h-3 w-3 shrink-0" />
              {t('settings.declined')}
            </span>
          );
        }
        return (
          <span className={`inline-flex items-center gap-1.5 text-xs whitespace-nowrap ${m.status === 'inactive' ? 'text-muted-foreground' : 'text-success'}`}>
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${m.status === 'inactive' ? 'bg-muted-foreground' : 'bg-success'}`} />
            {m.status === 'inactive' ? t('settings.inactive') : t('settings.active')}
          </span>
        );
      },
    },
    {
      id: 'joinedAt',
      label: t('settings.joined'),
      priority: 'tertiary',
      sortable: true,
      hideOn: 'tablet',
      ...TABLE_COL.date,
      defaultVisible: false,
      render: (m) => (
        <span className="text-muted-foreground">{formatRelative(m.joinedAt)}</span>
      ),
    },
    {
      id: 'actions',
      label: '',
      priority: 'hidden',
      alwaysVisible: true,
      ...TABLE_COL.actions,
      render: (m) => {
        const isPending = m.status === 'pending';
        if (!hook.canManageMembers || m.isOwner) return null;
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isPending && (
                  <DropdownMenuItem onClick={() => hook.copyInviteLink(m.id)}>
                    <Link2 className="h-3.5 w-3.5 mr-2" /> {t('settings.copyInviteLink')}
                  </DropdownMenuItem>
                )}
                {isPending && (
                  <DropdownMenuItem onClick={() => { void hook.handleResendInvitation(m.id); }}>
                    <Mail className="h-3.5 w-3.5 mr-2" /> {t('settings.resendInvite')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => hook.openEdit(m)}><Pencil className="h-3.5 w-3.5 mr-2" /> {t('common.edit')}</DropdownMenuItem>
                <DropdownMenuItem className="text-destructive" onClick={() => hook.setRemoveId(m.id)}><UserX className="h-3.5 w-3.5 mr-2" /> {t('common.remove')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  type TeamRow = CompanyTeam & { memberCount: number };

  const teamRows = useMemo(
    () => hook.teams.map((tm) => ({ ...tm, memberCount: tm.memberIds?.length ?? 0 })),
    [hook.teams],
  );

  const { sorted: sortedTeams, sort: teamsSort, toggleSort: toggleTeamsSort } = useSortableTable(
    teamRows,
    { column: 'name', direction: 'asc' },
  );

  const teamsColumns: ResponsiveTableColumn<TeamRow>[] = [
    {
      id: 'name',
      label: t('settings.teamName'),
      priority: 'primary',
      alwaysVisible: true,
      sortable: true,
      ...TABLE_COL.title,
      truncate: true,
      render: (tm) => <span className="font-medium truncate block">{tm.name}</span>,
    },
    {
      id: 'description',
      label: t('settings.teamDescription'),
      priority: 'secondary',
      sortable: true,
      truncate: true,
      ...TABLE_COL.subtitle,
      render: (tm) => (
        <span className="text-muted-foreground truncate block">{tm.description || '—'}</span>
      ),
    },
    {
      id: 'memberCount',
      label: t('settings.teamMembers'),
      priority: 'tertiary',
      sortable: true,
      ...TABLE_COL.count,
      render: (tm) => (
        <button
          type="button"
          className="text-sm font-medium text-primary hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            hook.handleTeamClick(tm.id, tm.name);
          }}
        >
          {tm.memberCount}
        </button>
      ),
    },
    {
      id: 'actions',
      label: '',
      priority: 'hidden',
      alwaysVisible: true,
      ...TABLE_COL.actions,
      render: (tm) => {
        if (!hook.canEditCompany) return null;
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { hook.setEditTeam({ id: tm.id, name: tm.name, description: tm.description || '' }); hook.setTeamDialog(true); }}><Pencil className="h-3.5 w-3.5 mr-2" /> {t('common.edit')}</DropdownMenuItem>
                <DropdownMenuItem className="text-destructive" onClick={() => hook.handleDeleteTeam(tm.id)}><Trash2 className="h-3.5 w-3.5 mr-2" /> {t('common.delete')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto w-full">
      <CompanyHeader
        name={hook.companyName || selectedCompany?.name || ''}
        publicName={hook.companyPublicName}
        logoUrl={hook.savedLogoUrl}
      />

      <Tabs value={hook.activeTab} onValueChange={v => {
        hook.setActiveTab(v);
        if (v !== 'members') hook.clearTeamFilter();
        const next = new URLSearchParams(searchParams);
        next.set('tab', v);
        setSearchParams(next, { replace: true });
      }}>
        <div className="flex shrink-0 min-w-0 items-center justify-between gap-2 pb-2">
          <div className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
            <TabsList className="inline-flex h-10 w-max min-w-0 justify-start">
              <TabsTrigger value="general">{t('settings.general')}</TabsTrigger>
              <TabsTrigger value="teams">{t('settings.teams')}</TabsTrigger>
              <TabsTrigger value="members">{t('settings.members')}</TabsTrigger>
              <TabsTrigger value="plan">{t('settings.plan')}</TabsTrigger>
              {/*
                v3.2 (21/05/26): Subscription tab moved to /profile?tab=billing.
                Removed from this TabsList; legacy ?tab=subscription URL redirects
                to /profile?tab=billing in the useEffect above.

                v3.2 polish (22/05/26): added read-only "plan" tab — see
                CompanyPlanTab.tsx for rationale.
              */}
            </TabsList>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {hook.activeTab === 'members' && hook.canManageMembers && (
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => { void hook.handleInviteMemberClick(); }}
                disabled={hook.checkingMemberCap}
              >
                <Mail className="h-3.5 w-3.5" /> {t('settings.inviteMember')}
              </Button>
            )}
            {hook.activeTab === 'teams' && hook.canEditCompany && (
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => { hook.setEditTeam({ name: '', description: '' }); hook.setTeamDialog(true); }}
              >
                <Plus className="h-3.5 w-3.5" /> {t('settings.addTeam')}
              </Button>
            )}
          </div>
        </div>

        {/* General */}
        <TabsContent value="general" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('settings.logo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {hook.savedLogoUrl ? (
                  <img
                    src={hook.savedLogoUrl}
                    alt={hook.companyName || selectedCompany?.name || 'Company logo'}
                    className="h-16 w-16 rounded-md object-contain border border-border bg-background shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-16 w-16 rounded-md border border-border bg-muted/30 flex items-center justify-center shrink-0 text-lg font-semibold text-muted-foreground">
                    {(hook.companyName || selectedCompany?.name)?.[0] || '?'}
                  </div>
                )}
                <input
                  id="logo-upload-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={hook.handleLogoFileSelected}
                  disabled={hook.uploadingLogo}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={hook.uploadingLogo}
                  >
                    <label htmlFor="logo-upload-input" className="cursor-pointer">
                      {hook.uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      {hook.savedLogoUrl ? t('settings.replaceLogo') : t('settings.uploadLogo')}
                    </label>
                  </Button>
                  {hook.savedLogoUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-destructive hover:text-destructive"
                      onClick={() => void hook.handleRemoveLogo()}
                      disabled={hook.uploadingLogo}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t('settings.removeLogo')}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('settings.companyInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('settings.companyName')}</Label>
                <Input value={hook.companyName} onChange={e => hook.setCompanyName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.publicName')}</Label>
                <Input value={hook.companyPublicName} onChange={e => hook.setCompanyPublicName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.description')}</Label>
                <Textarea value={hook.companyDescription} onChange={e => hook.setCompanyDescription(e.target.value)} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.employeeCount')}</Label>
                <Input
                  type="number"
                  value={hook.companyEmployeeCount}
                  onChange={e => hook.setCompanyEmployeeCount(e.target.value)}
                  placeholder="—"
                  className="max-w-[200px]"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>{t('settings.avgSalaryMin')}</Label>
                  <Input type="number" value={hook.avgSalaryMin} onChange={e => hook.setAvgSalaryMin(e.target.value)} placeholder="—" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.avgSalaryMax')}</Label>
                  <Input type="number" value={hook.avgSalaryMax} onChange={e => hook.setAvgSalaryMax(e.target.value)} placeholder="—" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.currency')}</Label>
                  <Select
                    value={hook.avgSalaryCurrency || 'EUR'}
                    onValueChange={hook.setAvgSalaryCurrency}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR — €</SelectItem>
                      <SelectItem value="USD">USD — $</SelectItem>
                      <SelectItem value="GBP">GBP — £</SelectItem>
                      <SelectItem value="CHF">CHF — Fr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('settings.offices')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {hook.offices.length === 0 && (
                <p className="text-xs text-muted-foreground py-1">{t('settings.offices.empty')}</p>
              )}
              {hook.offices.map((o, i) => (
                <div key={i} className="rounded-md border border-border/50 bg-muted/20 p-3">
                  <div className="flex items-center gap-2">
                    <Select
                      value={hook.OFFICE_TYPES.includes(o.type) ? o.type : '__other'}
                      onValueChange={v => hook.setOffices(prev => prev.map((x, j) => j === i ? { ...x, type: v === '__other' ? '' : v } : x))}
                    >
                      <SelectTrigger className="w-[140px] shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {hook.OFFICE_TYPES.map(ot => <SelectItem key={ot} value={ot}>{ot}</SelectItem>)}
                        <SelectItem value="__other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {(!hook.OFFICE_TYPES.includes(o.type) || o.type === '') && (
                      <Input
                        value={o.type}
                        onChange={e => hook.setOffices(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                        placeholder="Custom type..."
                        className="w-[120px] shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <AddressAutocomplete
                        value={o.address}
                        onChange={v => hook.setOffices(prev => prev.map((x, j) => j === i ? { ...x, address: v } : x))}
                        className="w-full"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-destructive"
                      onClick={() => hook.setOffices(prev => prev.filter((_, j) => j !== i))}
                      aria-label="Remove office"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-1.5"
                onClick={() => hook.setOffices(prev => [...prev, { type: hook.OFFICE_TYPES[0] || '', address: '' }])}
              >
                <Plus className="h-3.5 w-3.5" /> {t('settings.offices.addCta')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('settings.links')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {hook.links.length === 0 && (
                <p className="text-xs text-muted-foreground py-1">{t('settings.links.empty')}</p>
              )}
              {hook.links.map((l, i) => (
                <div key={i} className="rounded-md border border-border/50 bg-muted/20 p-3">
                  <div className="flex items-center gap-2">
                    <Select
                      value={hook.LINK_TYPES.includes(l.type) ? l.type : '__other'}
                      onValueChange={v => hook.setLinks(prev => prev.map((x, j) => j === i ? { ...x, type: v === '__other' ? '' : v } : x))}
                    >
                      <SelectTrigger className="w-[140px] shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {hook.LINK_TYPES.map(lt => <SelectItem key={lt} value={lt}>{lt}</SelectItem>)}
                        <SelectItem value="__other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {(!hook.LINK_TYPES.includes(l.type) || l.type === '') && (
                      <Input
                        value={l.type}
                        onChange={e => hook.setLinks(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                        placeholder="Custom type..."
                        className="w-[120px] shrink-0"
                      />
                    )}
                    <Input
                      value={l.address}
                      onChange={e => hook.setLinks(prev => prev.map((x, j) => j === i ? { ...x, address: e.target.value } : x))}
                      placeholder="https://..."
                      className="flex-1 min-w-0"
                      title={l.address || undefined}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-destructive"
                      onClick={() => hook.setLinks(prev => prev.filter((_, j) => j !== i))}
                      aria-label="Remove link"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-1.5"
                onClick={() => hook.setLinks(prev => [...prev, { type: hook.LINK_TYPES[0] || '', address: '' }])}
              >
                <Plus className="h-3.5 w-3.5" /> {t('settings.links.addCta')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('settings.defaultOutputLanguage')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Select
                value={hook.defaultOutputLanguage}
                onValueChange={(v) => hook.setDefaultOutputLanguage(v as 'it' | 'en')}
              >
                <SelectTrigger className="max-w-[260px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">{t('positions.outputLanguageIt')}</SelectItem>
                  <SelectItem value="en">{t('positions.outputLanguageEn')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('settings.defaultOutputLanguageHint')}
              </p>
            </CardContent>
          </Card>

          {/* Save button — admin/write only */}
          {hook.canEditCompany && (
            <Button size="sm" onClick={hook.handleSaveGeneral} disabled={hook.savingGeneral}>
              {hook.savingGeneral && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('settings.saveChanges')}
            </Button>
          )}

          {/* Delete Company — admin only (BE enforces owner-only) */}
          {hook.canManageMembers && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/50 px-4 py-3">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-sm font-medium">{t('settings.deleteCompanyTitle')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('settings.deleteCompanyDesc', { name: selectedCompany?.name || '' })}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => hook.setDeleteCompanyOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('common.delete')}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Members */}
        <TabsContent value="members" className="mt-4 space-y-4">
          {hook.teamFilterName && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('settings.filteredBy')} <strong>{hook.teamFilterName}</strong></span>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={hook.clearTeamFilter}>{t('settings.clear')}</Button>
            </div>
          )}

          <ListTablePanel showFooter={false}>
            <ResponsiveTable
              data={sorted}
              columns={membersColumns}
              getRowKey={(m) => m.id}
              storageKey="settings-members"
              sort={sort}
              onSortChange={toggleSort}
              emptyState={
                <div className="py-12 text-center text-muted-foreground text-sm">{t('common.noResults')}</div>
              }
              ariaLabel={t('settings.members')}
            />
          </ListTablePanel>

          {/* Invite Member Dialog — Sprint 6 (S6.A): firstName + lastName
              fields + role/type help text so recruiters understand what
              "internal vs external" and "admin vs write vs read" actually
              control. The fields are optional — leaving them blank still
              sends an invitation, just with a generic greeting. */}
          <Dialog
            open={hook.addOpen}
            onOpenChange={(open) => {
              if (hook.isSendingInvite) return;
              hook.setAddOpen(open);
            }}
          >
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>{t('settings.inviteMember')}</DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2">{t('settings.inviteDesc')}</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('settings.firstName')}</Label>
                    <Input value={hook.addFirstName} onChange={e => hook.setAddFirstName(e.target.value)} placeholder="Mario" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('settings.lastName')}</Label>
                    <Input value={hook.addLastName} onChange={e => hook.setAddLastName(e.target.value)} placeholder="Rossi" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.email')} *</Label>
                  <Input value={hook.addEmail} onChange={e => hook.setAddEmail(e.target.value)} placeholder="name@company.com" />
                </div>
                  <div className="grid grid-cols-2 gap-3">
                   <div className="space-y-2">
                     <Label>{t('settings.role')} *</Label>
                     <Select value={hook.addPermission} onValueChange={v => hook.setAddPermission(v as Permission)}>
                       <SelectTrigger><SelectValue /></SelectTrigger>
                       <SelectContent>
                         <SelectItem value="admin">Admin — full control</SelectItem>
                         <SelectItem value="write">Write — create &amp; edit</SelectItem>
                         <SelectItem value="read">Read — view only</SelectItem>
                       </SelectContent>
                     </Select>
                     <p className="text-[11px] text-muted-foreground">{t('settings.roleHint')}</p>
                   </div>
                  <div className="space-y-2">
                   <Label>{t('settings.type')}</Label>
                    <Select value={hook.addType} onValueChange={v => hook.setAddType(v as MemberType)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">{t('settings.internal')}</SelectItem>
                        <SelectItem value="external">{t('settings.external')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">{t('settings.typeHint')}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.team')}</Label>
                  <Select value={hook.addTeam} onValueChange={hook.setAddTeam}>
                    <SelectTrigger><SelectValue placeholder={t('settings.selectTeam')} /></SelectTrigger>
                    <SelectContent>
                      {hook.teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => hook.setAddOpen(false)}
                  disabled={hook.isSendingInvite}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={() => { void hook.handleAddMember(); }}
                  disabled={!hook.addEmail || hook.isSendingInvite}
                >
                  {hook.isSendingInvite ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {t('settings.sendInvite')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Member Dialog */}
          <Dialog open={!!hook.editMember} onOpenChange={() => hook.setEditMember(null)}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>{t('settings.editMember')}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                 <div className="space-y-2">
                   <Label>{t('settings.role')}</Label>
                   <Select value={hook.editPermission} onValueChange={v => hook.setEditPermission(v as Permission)}>
                     <SelectTrigger><SelectValue /></SelectTrigger>
                     <SelectContent>
                       <SelectItem value="admin">Admin — full control</SelectItem>
                       <SelectItem value="write">Write — create &amp; edit</SelectItem>
                       <SelectItem value="read">Read — view only</SelectItem>
                     </SelectContent>
                   </Select>
                 </div>
                <div className="space-y-2">
                  <Label>{t('settings.type')}</Label>
                  <Select value={hook.editType} onValueChange={v => hook.setEditType(v as MemberType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">{t('settings.internal')}</SelectItem>
                      <SelectItem value="external">{t('settings.external')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.team')}</Label>
                  <Select value={hook.editMemberTeam} onValueChange={hook.setEditMemberTeam}>
                    <SelectTrigger><SelectValue placeholder={t('settings.selectTeam')} /></SelectTrigger>
                    <SelectContent>
                      {hook.teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <Label>{t('settings.active')}</Label>
                  <Switch checked={hook.editActive} onCheckedChange={hook.setEditActive} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => hook.setEditMember(null)}>{t('common.cancel')}</Button>
                <Button onClick={hook.handleEditSave}>{t('common.save')}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Remove confirmation */}
          <Dialog open={!!hook.removeId} onOpenChange={() => hook.setRemoveId(null)}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>{t('settings.removeMember')}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">{t('settings.removeConfirm')}</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => hook.setRemoveId(null)}>{t('common.cancel')}</Button>
                <Button variant="destructive" onClick={hook.handleRemove}>{t('common.remove')}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Teams */}
        <TabsContent value="teams" className="mt-4 space-y-4">
          <ListTablePanel showFooter={false}>
            <ResponsiveTable
              data={sortedTeams}
              columns={teamsColumns}
              getRowKey={(tm) => tm.id}
              storageKey="settings-teams"
              sort={teamsSort}
              onSortChange={toggleTeamsSort}
              emptyState={
                <div className="py-12 text-center text-muted-foreground text-sm">{t('common.noResults')}</div>
              }
              ariaLabel={t('settings.teams')}
            />
          </ListTablePanel>

          {/* Team dialog — admin/write only */}
          {hook.canEditCompany && (
            <Dialog open={hook.teamDialog} onOpenChange={hook.setTeamDialog}>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>{hook.editTeam.id ? t('settings.editTeam') : t('settings.addTeam')}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('settings.teamName')} *</Label>
                    <Input value={hook.editTeam.name} onChange={e => hook.setEditTeam(prev => ({ ...prev, name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('settings.teamDescription')}</Label>
                    <Input value={hook.editTeam.description} onChange={e => hook.setEditTeam(prev => ({ ...prev, description: e.target.value }))} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => hook.setTeamDialog(false)}>{t('common.cancel')}</Button>
                  <Button onClick={hook.handleSaveTeam} disabled={!hook.editTeam.name}>{t('common.save')}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </TabsContent>

        {/* Subscription */}
        {/* v3.2 (21/05/26): TabsContent "subscription" removed — moved to /profile?tab=billing */}

        {/* Plan (read-only) — v3.2 polish 22/05/26 */}
        <TabsContent value="plan" className="mt-4">
          <CompanyPlanTab />
        </TabsContent>
      </Tabs>

      {/* Delete Company Confirmation */}
      <Dialog open={hook.deleteCompanyOpen} onOpenChange={hook.setDeleteCompanyOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('settings.deleteCompanyTitle')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('settings.deleteCompanyConfirm', { name: selectedCompany?.name || '' })}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => hook.setDeleteCompanyOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={hook.handleDeleteCompany}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
